/*
 * Decléor — Collection colours
 * ---------------------------------------------------------------------------
 * Colours every occurrence of a Decléor collection name (Aromessence ranges)
 * in its signature colour, site-wide. Data source: assets/colors.json (passed
 * as data-colors-url on this script tag) — edit that file to change colours.
 *
 * Liquid cannot read assets/*.json server-side, so the mapping is fetched and
 * applied on the client. Names are matched across all locales (fr/en/it) so
 * the localised name gets coloured on every language's pages.
 *
 * Matched text is wrapped in <span data-collection-color style="color:…">.
 * The span carries a marker so re-scans never double-wrap, and the colour is
 * set with `important` so it wins over link/heading colour rules.
 */
(function () {
  'use strict';

  var script = document.currentScript || document.querySelector('script[data-colors-url]');
  var url = script && script.getAttribute('data-colors-url');
  if (!url || !window.MutationObserver || !document.createTreeWalker) {
    return;
  }

  var MARKER = 'data-collection-color';
  /* Set on a LINK whose entire text is one coloured span, so CSS can move the underline onto that span
   * and have it take the colour. The link cannot answer this in CSS: `:has(> [data-collection-color])`
   * is true for a partial match too, and `:only-child` counts element children only — so
   * `<a>Crème Rosée <span>Magnolia Blanc</span></a>` matched it and lost the underline under "Crème
   * Rosée", while a section rule elsewhere re-added one and the result was two underlines.
   * Here the answer is exact: after wrapping, the link is whole if its own text, trimmed, is the same
   * as the span's. */
  var WHOLE = 'data-collection-color-whole';
  var SKIP = 'data-collection-color-skip';
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1,
    OPTION: 1, CODE: 1, PRE: 1, SVG: 1, svg: 1
  };

  var regex = null;
  var lookup = {};
  var running = false;
  var scheduled = false;

  function normalise(value) {
    return value
      .toLowerCase()
      .replace(/[‘’‛]/g, "'") // smart apostrophes -> '
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function build(data) {
    var names = [];
    var colors = (data && data.colors) || [];
    colors.forEach(function (entry) {
      if (!entry || !entry.hex) {
        return;
      }
      var hex = '#' + String(entry.hex).replace(/^#/, '');
      // A locale may list several spellings — the translated titles are not consistent (IT writes
      // both "Magnolia Bianca" and "Magnolia Bianco"; EN moved from "Oriental Rose" / "Rosemary" to
      // the marketing names "Rose Damascena" / "Rosemary Officinalis" in October 2026, and older copy
      // still says the former), so each name_* is a string or an array of strings.
      ['name_fr', 'name_en', 'name_it'].forEach(function (key) {
        var list = entry[key];
        var n;
        if (!list) {
          return;
        }
        if (typeof list === 'string') {
          list = [list];
        }
        for (n = 0; n < list.length; n += 1) {
          if (list[n]) {
            lookup[normalise(list[n])] = hex;
            names.push(list[n]);
          }
        }
      });
    });

    // De-dupe and match longest names first so overlaps resolve correctly.
    names = names
      .filter(function (value, index, all) { return all.indexOf(value) === index; })
      .sort(function (a, b) { return b.length - a.length; });
    if (!names.length) {
      return;
    }

    var pattern = names
      .map(function (name) { return escapeRegExp(name).replace(/'/g, "['‘’‛]"); })
      .join('|');

    // Not preceded/followed by a letter or number, so we never match inside a word.
    regex = new RegExp('(?<![\\p{L}\\p{N}])(' + pattern + ')(?![\\p{L}\\p{N}])', 'giu');
  }

  function shouldSkip(node) {
    for (var el = node.parentNode; el && el.nodeType === 1; el = el.parentNode) {
      if (SKIP_TAGS[el.tagName]) {
        return true;
      }
      // SKIP : exclusion explicite d'un bloc entier, pour un design qui fixe lui-même la couleur des
      // noms de gamme (sections de campagne cle-dor-*, où la maquette les veut en noir).
      if (el.hasAttribute(MARKER) || el.hasAttribute(SKIP) || el.isContentEditable) {
        return true;
      }
    }
    return false;
  }

  function colorize(root) {
    if (!regex || !root || !root.querySelectorAll) {
      return;
    }
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.length < 3) {
          return NodeFilter.FILTER_REJECT;
        }
        if (shouldSkip(node)) {
          return NodeFilter.FILTER_REJECT;
        }
        regex.lastIndex = 0;
        return regex.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    var matches = [];
    var current;
    while ((current = walker.nextNode())) {
      matches.push(current);
    }

    var changed = 0;

    matches.forEach(function (node) {
      var text = node.nodeValue;
      var fragment = document.createDocumentFragment();
      var lastIndex = 0;
      var match;
      regex.lastIndex = 0;
      while ((match = regex.exec(text))) {
        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        var span = document.createElement('span');
        span.setAttribute(MARKER, '');
        var hex = lookup[normalise(match[0])];
        if (hex) {
          span.style.setProperty('color', hex, 'important');
        }
        span.textContent = match[0];
        fragment.appendChild(span);
        lastIndex = match.index + match[0].length;
        if (match.index === regex.lastIndex) {
          regex.lastIndex++;
        }
      }
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      if (fragment.childNodes.length && node.parentNode) {
        var host = node.parentNode;
        host.replaceChild(fragment, node);
        markWholeLink(host);
        changed += 1;
      }
    });

    return changed;
  }

  /* Walk up from the node that was just recoloured to the nearest link, and mark it when the coloured
     span accounts for all of the link's text. Cheap: one closest() and two string reads, and only on
     the nodes that actually matched a collection name. */
  function markWholeLink(host) {
    var link = host.closest ? host.closest('a') : null;
    if (!link) {
      return;
    }
    var spans = link.querySelectorAll('[' + MARKER + ']');
    if (spans.length !== 1) {
      link.removeAttribute(WHOLE);
      return;
    }
    if (link.textContent.trim() === spans[0].textContent.trim()) {
      link.setAttribute(WHOLE, '');
    } else {
      link.removeAttribute(WHOLE);
    }
  }

  // Expose each product card's Aromessence colour as --aroma-color (matched from
  // its data-cutline via colors.json) so CSS can use it for the hover veil.
  function tintCards(scope) {
    var cards = (scope || document).querySelectorAll('product-grid-item[data-cutline], .product-grid-item[data-cutline]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].style.getPropertyValue('--aroma-color')) {
        continue;
      }
      var hex = lookup[normalise(cards[i].getAttribute('data-cutline') || '')];
      if (hex) {
        cards[i].style.setProperty('--aroma-color', hex);
      }
    }
  }

  /* TELL ANY FLICKITY CAROUSEL THAT ITS CARDS JUST GREW.
   *
   * Wrapping a range name in a span is not a neutral edit: snippets/product-card-style.liquid turns
   * `.product__grid__title:has([data-collection-color])` into a column flexbox, so the title goes
   * from one line to two the moment the span lands. Measured on the related-products carousel:
   * the title 22.94px -> 37.16px, the card 466.05px -> 480.27px, +14.22px each.
   *
   * Flickity absolutely positions its cells (theme.css makes `.flickity-cell` position:absolute
   * !important), so the cards contribute no height at all and the viewport's height is an inline
   * pixel value Flickity writes ONCE, from the tallest cell it has measured. Grow the cards after
   * that and the viewport keeps the old number with `overflow: hidden` over it — the bottom 14.22px
   * of every card is simply sliced off, which lands exactly on the price and the star rating.
   *
   * It is a race, which is why it comes and goes: the colours are fetched, then applied inside a
   * requestIdleCallback, while theme.js re-inits the carousel synchronously the moment the
   * recommendations HTML is injected. Win the race and nothing shows; lose it — a busy main thread,
   * and this page loads GTM, Judge.me, Affilae, Klaviyo and the cart app — and the clip is permanent
   * until something resizes the window. Measured both ways: at 1x CPU the viewport settled at
   * 480.266px and nothing clipped; at 6x throttling it froze at 466.047px, the one-line height, and
   * six of the seven cards lost 14.22px.
   *
   * resize() is Flickity's own re-measure, and the theme already treats it as the sanctioned way to
   * say "your cells changed": theme.js does exactly this on `theme:tab:change` (assets/theme.js:1202).
   *
   * REACHED THROUGH THE CUSTOM ELEMENT, NOT THROUGH window.Flickity. The obvious call is
   * `Flickity.data(el).resize()` — it was written that way first and it silently did nothing,
   * because this bundle keeps the constructor inside its own closure: measured on the page,
   * `typeof window.Flickity` is "undefined" while six carousels are running. The theme's
   * <flickity-carousel> element (assets/theme.js:1116-1199, registered at 1380) stores its instance
   * on itself as `carouselInstance`, which is a plain public property on a plain DOM node. That is
   * the handle, and it is the same one theme.js reaches for internally.
   *
   * Guarded so it costs nothing on the pages that need nothing: only when a span was actually
   * inserted this run, and only where an instance really exists. */
  /* KEEP A CARD TITLE'S RANGE LINE IN ONE PIECE.
   *
   * snippets/product-card-style.liquid turns a title holding a range span into a column flexbox, so
   * every child becomes its own line. That is right for "AROMESSENCE NÉROLI BIGARADE" (two children),
   * but a title with the range in the MIDDLE has three: "DUO BOOSTER ÉCLAT - MANDARINE VERTE | 2 SOINS"
   * printed "DUO BOOSTER ÉCLAT -" / "MANDARINE VERTE" / "| 2 SOINS", with the dash dangling at the end
   * of the first line and the count alone on the last.
   *
   * So when text follows the span, the span and that text are wrapped together into one flex item
   * ("MANDARINE VERTE | 2 SOINS"), and a separator left at the end of the text before it (- – — | : ,)
   * is dropped, since the line break now does its job. Only when real text precedes the span: a title
   * that OPENS on the range keeps its range on the first line as before. The visible title is the only
   * thing touched — the card link already carries the full title as its aria-label.
   * Idempotent: once wrapped, the span's parent is no longer the title, so a second pass skips it. */
  var TITLE_LINE = 'dc-title-range';
  var TRAILING_SEPARATOR = /[\s\-–—|:,]+$/;
  var LEADING_SPACE = /^\s+/;

  function hasText(first) {
    for (var node = first; node; node = node.nextSibling) {
      if ((node.textContent || '').trim()) {
        return true;
      }
    }
    return false;
  }

  function lockTitleLines(scope) {
    var titles = scope.querySelectorAll('.product__grid__title, .product-item__title');
    var changed = 0;
    var i;
    var title;
    var span;
    var prev;
    var wrap;
    var suffix;

    for (i = 0; i < titles.length; i += 1) {
      title = titles[i];
      span = title.querySelector('[' + MARKER + ']');
      if (!span || span.parentNode !== title) {
        continue;
      }
      prev = span.previousSibling;
      if (!prev || prev.nodeType !== 3 || !prev.nodeValue.trim()) {
        continue;
      }
      prev.nodeValue = prev.nodeValue.replace(TRAILING_SEPARATOR, ' ');
      if (!hasText(span.nextSibling)) {
        continue;
      }
      wrap = document.createElement('span');
      wrap.className = TITLE_LINE;
      title.insertBefore(wrap, span);
      wrap.appendChild(span);
      // The trailing text ("| 2 SOINS") becomes one unbreakable unit: on a card too narrow for the whole
      // line it moves under the range as a block instead of splitting between "2" and "SOINS".
      suffix = document.createElement('span');
      suffix.className = TITLE_LINE + '__suffix';
      while (wrap.nextSibling) {
        suffix.appendChild(wrap.nextSibling);
      }
      // The space before "|" stays OUTSIDE the unbreakable unit: inside it, it would weld the unit to
      // the range name and the only place left to break would be between "MANDARINE" and "VERTE".
      if (suffix.firstChild && suffix.firstChild.nodeType === 3 && LEADING_SPACE.test(suffix.firstChild.nodeValue)) {
        suffix.firstChild.nodeValue = suffix.firstChild.nodeValue.replace(LEADING_SPACE, '');
        wrap.appendChild(document.createTextNode(' '));
      }
      wrap.appendChild(suffix);
      changed += 1;
    }
    return changed;
  }

  function remeasure(scope) {
    var carousels = scope.querySelectorAll('flickity-carousel');
    var instance;
    var i;

    for (i = 0; i < carousels.length; i += 1) {
      instance = carousels[i].carouselInstance;
      if (instance && typeof instance.resize === 'function') {
        instance.resize();
      }
    }
  }

  /* ── OÙ SCANNER ────────────────────────────────────────────────────────────────────────────────
   * L'observateur sait déjà quels sous-arbres ont été insérés ; run() jetait cette information et
   * repartait de document.body à chaque fois.
   *
   * Mesuré sur la page d'accueil, tiroir panier ouvert, UN changement de quantité : colorize() sur
   * document.body coûte 11,2 ms par passe et la passe est jouée 4 fois — ~50 ms. Avec le processeur
   * bridé 4x : 113 ms répartis en deux tâches longues de plus de 50 ms chacune. Pour comparaison,
   * l'observateur de freegift-modal.js coûte 1,2 ms sur la même fenêtre : c'est pourquoi celui-là
   * reste tel quel et celui-ci change.
   *
   * La raison est l'étendue, pas la fréquence : document.body porte 5469 nœuds texte, le tiroir 386.
   * Scanner le sous-arbre réellement inséré revient 24 fois moins cher (0,46 ms contre 11,2).
   *
   * tintCards RESTE GLOBAL : il coûte 0,07 ms pour 43 cartes et il doit pouvoir teinter une carte
   * dont la couleur vient d'un frère, pas d'elle-même. Le restreindre casserait ça pour rien. */
  var pending = [];

  function collect(nodes) {
    var i;
    var node;
    for (i = 0; i < nodes.length; i += 1) {
      node = nodes[i];
      /* Les nœuds texte insérés seuls sont traités par leur parent : le TreeWalker part d'un
         élément. Un nœud sans parent est déjà détaché, il n'y a rien à peindre. */
      if (node.nodeType === 3 && node.parentNode) {
        node = node.parentNode;
      }
      if (node.nodeType === 1 && pending.indexOf(node) === -1) {
        pending.push(node);
      }
    }
  }

  function run() {
    var roots = pending.length ? pending : [document.body];
    var changed = 0;
    var i;

    if (running) {
      return;
    }
    running = true;
    pending = [];
    try {
      for (i = 0; i < roots.length; i += 1) {
        /* Un sous-arbre retiré du document entre l'observation et la passe au repos n'a plus rien à
           montrer. document.contains couvre aussi le cas où il a été remplacé. */
        if (roots[i] === document.body || document.contains(roots[i])) {
          changed += colorize(roots[i]);
        }
      }
      tintCards(document.body);
      changed += lockTitleLines(document.body);
      if (changed) {
        remeasure(document.body);
      }
    } finally {
      running = false;
    }
  }

  function schedule() {
    if (scheduled || running) {
      return;
    }
    scheduled = true;
    (window.requestIdleCallback || window.requestAnimationFrame || function (cb) { return setTimeout(cb, 150); })(function () {
      scheduled = false;
      run();
    });
  }

  function start() {
    run();
    // Catch dynamically injected content (cart drawer, quickview, filtered
    // grids, predictive search, page transitions…). Our own span insertions
    // produce no new matches, so this never loops.
    var observer = new MutationObserver(function (mutations) {
      var i;
      var any = false;
      for (i = 0; i < mutations.length; i += 1) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          collect(mutations[i].addedNodes);
          any = true;
        }
      }
      if (any) {
        schedule();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  fetch(url)
    .then(function (response) { return response.json(); })
    .then(function (data) {
      build(data);
      if (!regex) {
        return;
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }
    })
    .catch(function () { /* colours are a progressive enhancement */ });
})();
